/**
 * OAuth 2.0 PKCE contre Twenty (par utilisateur), client PUBLIC : aucun secret
 * (token_endpoint_auth_method=none) — la PWA ne porte donc jamais de credential.
 *
 * - access_token : en MÉMOIRE uniquement (jamais sur disque -> vol par XSS limité).
 * - refresh_token : localStorage -> session PERSISTANTE (survit à la fermeture/réouverture
 *   de la PWA installée : pas de re-login à chaque lancement, restaurée via le refresh OAuth
 *   au démarrage). Le verifier PKCE + l'état transitent aussi par localStorage le temps de
 *   la redirection (pour survivre au round-trip cross-origin d'une PWA installée), puis sont
 *   effacés dès l'échange.
 *
 * Endpoints Twenty (vérifiés via /.well-known/oauth-authorization-server) :
 * authorization_endpoint = `/authorize` (PAS /oauth/authorize, qui n'est qu'un catch-all
 * SPA), token_endpoint = POST /oauth/token. Scope `api profile`, client public PKCE (none).
 */
const TWENTY_URL = import.meta.env.VITE_TWENTY_URL ?? '';
const CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID ?? '';
const REDIRECT_URI = import.meta.env.VITE_OAUTH_REDIRECT_URI ?? '';
const CLE_VERIFIER = 'ocerm_pkce_verifier';
const CLE_ETAT = 'ocerm_oauth_etat';
const CLE_REFRESH = 'ocerm_oauth_refresh';
/** Marge avant expiration en deçà de laquelle on rafraîchit de façon proactive. */
const MARGE_REFRESH_MS = 30_000;

let jetonAcces: string | null = null;
let expireA = 0; // ms epoch d'expiration de l'access_token
/** Résultat d'un refresh : 'ok' réussi ; 'invalide' refresh rejeté définitivement
 *  (400/401 ou aucun refresh) -> session coupée ; 'transitoire' panne réseau/5xx ->
 *  on GARDE le refresh_token pour réessayer (jamais de déconnexion sur coupure passagère). */
type EtatRefresh = 'ok' | 'invalide' | 'transitoire';
let refreshEnCours: Promise<EtatRefresh> | null = null;

export function oauthConfigure(): boolean {
  return Boolean(TWENTY_URL && CLIENT_ID && REDIRECT_URI);
}

export function estConnecte(): boolean {
  return jetonAcces !== null;
}

export function deconnecter(): void {
  jetonAcces = null;
  expireA = 0;
  try {
    localStorage.removeItem(CLE_REFRESH);
  } catch {
    /* localStorage indisponible : rien à nettoyer */
  }
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function aleatoire(n = 32): string {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** Démarre la connexion : redirige vers la page de login Twenty. */
export async function seConnecter(): Promise<void> {
  const verifier = aleatoire();
  const etat = aleatoire(16);
  // localStorage (et NON sessionStorage) : dans une PWA INSTALLÉE, l'aller vers Twenty
  // (cross-origin) puis le retour vers la PWA peut repartir dans un contexte de session
  // neuf -> sessionStorage vidé -> verifier perdu -> échange impossible au retour
  // (symptôme « formulaire OK mais retour KO »). localStorage est partagé par origine et
  // survit au round-trip ; il est effacé dès l'échange (finaliserConnexion).
  try {
    localStorage.setItem(CLE_VERIFIER, verifier);
    localStorage.setItem(CLE_ETAT, etat);
  } catch {
    /* stockage indisponible (navigation privée) : le retour échouera proprement */
  }
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'api profile',
    state: etat,
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
  });
  // Endpoint d'autorisation RÉEL = `/authorize` (le authorization_endpoint du discovery
  // doc). `/oauth/authorize` n'est qu'un catch-all SPA : il tombe sur /welcome (login) puis
  // sur le workspace (jamais la page de consentement) -> aucun code émis (c'était LE bug).
  // Navigation top-level (pas de popup : window.opener est coupé en PWA installée) ; le
  // retour ?code revient en top-level sur la PWA (redirect_uri in-scope).
  window.location.assign(`${TWENTY_URL}/authorize?${params}`);
}

/** Applique une réponse de /oauth/token : access en mémoire, refresh en sessionStorage. */
function appliquerJetons(data: {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}): boolean {
  if (!data.access_token) return false;
  jetonAcces = data.access_token;
  expireA = Date.now() + (data.expires_in ?? 3600) * 1000;
  if (data.refresh_token) {
    try {
      localStorage.setItem(CLE_REFRESH, data.refresh_token);
    } catch {
      /* localStorage indisponible : la session ne survivra pas au rechargement */
    }
  }
  return true;
}

async function echangerToken(
  corps: Record<string, string>,
): Promise<{ ok: boolean; statut: number }> {
  let res: Response;
  try {
    res = await fetch(`${TWENTY_URL}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(corps),
    });
  } catch {
    return { ok: false, statut: 0 }; // erreur réseau -> transitoire
  }
  if (!res.ok) return { ok: false, statut: res.status };
  return {
    ok: appliquerJetons((await res.json()) as Record<string, unknown>),
    statut: res.status,
  };
}

/**
 * Au retour de redirection : échange le code contre des jetons. À appeler au
 * démarrage de l'app. Renvoie true si une connexion a été finalisée.
 */
export async function finaliserConnexion(): Promise<boolean> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const etat = url.searchParams.get('state');
  if (!code) return false;

  let verifier: string | null = null;
  let etatAttendu: string | null = null;
  try {
    verifier = localStorage.getItem(CLE_VERIFIER);
    etatAttendu = localStorage.getItem(CLE_ETAT);
    localStorage.removeItem(CLE_VERIFIER);
    localStorage.removeItem(CLE_ETAT);
  } catch {
    /* stockage indisponible : verifier reste null -> on abandonne proprement plus bas */
  }
  // Nettoie l'URL (retire code/state).
  window.history.replaceState({}, '', url.pathname);
  if (!verifier || etat !== etatAttendu) return false;

  return (
    await echangerToken({
      grant_type: 'authorization_code',
      code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: verifier,
    })
  ).ok;
}

/** Rafraîchit l'access_token via le refresh_token (localStorage). Renvoie l'état :
 *  'ok' / 'invalide' (refresh rejeté -> session COUPÉE ici) / 'transitoire' (panne
 *  réseau/5xx -> session PRÉSERVÉE, à réessayer). Une coupure passagère ne déconnecte
 *  donc jamais l'utilisateur. */
export async function rafraichir(): Promise<EtatRefresh> {
  // Dédoublonne les refresh concurrents (restauration au démarrage + appels API
  // parallèles) : sinon deux appels dépensent le MÊME refresh_token ; si Twenty fait
  // tourner les refresh, l'un réussit, l'autre échoue et déconnecterait l'utilisateur
  // alors qu'une session fraîche existe. Un seul échange en vol, partagé par tous.
  if (refreshEnCours) return refreshEnCours;
  refreshEnCours = (async (): Promise<EtatRefresh> => {
    let refresh: string | null = null;
    try {
      refresh = localStorage.getItem(CLE_REFRESH);
    } catch {
      refresh = null;
    }
    // Aucun refresh disponible (jamais connecté, ou localStorage vidé/indisponible
    // après login) : état DÉFINITIF, on coupe la session ici. Sinon, avec un access
    // token expiré encore en mémoire, estConnecte() resterait « connecté » à tort et
    // des requêtes partiraient sans Bearer (signalé en revue Codex).
    if (!refresh) {
      deconnecter();
      return 'invalide';
    }
    const r = await echangerToken({
      grant_type: 'refresh_token',
      refresh_token: refresh,
      client_id: CLIENT_ID,
    });
    if (r.ok) return 'ok';
    // DÉFINITIF (refresh rejeté : invalid_grant 400 / unauthorized 401) -> on coupe la
    // session ICI (le refresh_token est garbage). TRANSITOIRE (réseau = statut 0, ou
    // 5xx) -> on GARDE le refresh_token et la session pour réessayer (sinon une coupure
    // réseau au démarrage = déconnexion permanente, bug signalé en revue).
    if (r.statut === 400 || r.statut === 401) {
      deconnecter();
      return 'invalide';
    }
    return 'transitoire';
  })();
  try {
    return await refreshEnCours;
  } finally {
    refreshEnCours = null;
  }
}

/** Au démarrage : si un refresh_token a survécu au rechargement, restaure la session
 *  sans re-login. Renvoie true si une session a été restaurée. */
export async function restaurerSession(): Promise<boolean> {
  if (jetonAcces) return true;
  return (await rafraichir()) === 'ok';
}

/** Jeton d'accès VALIDE : rafraîchi de façon proactive s'il est proche de l'expiration.
 *  null si non connecté ou refresh échoué. À utiliser avant chaque appel API. */
export async function jetonValide(): Promise<string | null> {
  if (jetonAcces && Date.now() < expireA - MARGE_REFRESH_MS) return jetonAcces;
  // Expiré / proche de l'expiration : on tente un refresh.
  // - 'ok' -> jeton frais.
  // - 'invalide' -> rafraichir a DÉJÀ déconnecté (refresh rejeté), on renvoie null.
  // - 'transitoire' -> on PRÉSERVE la session (panne réseau passagère) ; null pour cet
  //   appel, l'appelant réessaiera. Plus de déconnexion sur 5xx/réseau (bug corrigé).
  return (await rafraichir()) === 'ok' ? jetonAcces : null;
}
